import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { makeStateKey, TransferState } from '@angular/core';
import { CompaniesService } from '../../services/companies.service';
import { Company } from '../../models/company.model';
import { CompanyCardComponent } from '../../components/company-card/company-card.component';
import { LoadingComponent } from '../../components/shared/loading/loading.component';

@Component({
  selector: 'app-companies-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    CompanyCardComponent,
    LoadingComponent,
  ],
  templateUrl: './companies-page.component.html',
  styleUrls: ['./companies-page.component.scss'],
})
export class CompaniesPageComponent implements OnInit {
  companies: Company[] = [];
  totalCompanies = 0;
  isLoading = true;
  searchKeyword = '';
  currentPage = 0;
  pageSize = 12;

  private readonly isBrowser: boolean;
  private readonly isServer: boolean;

  constructor(
    private companiesService: CompaniesService,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.isServer = isPlatformServer(platformId);
  }

  ngOnInit(): void {
    this.loadCompanies();
  }

  loadCompanies(): void {
    const stateKey = this.getStateKey();

    // On browser, check if we have transferred state from server
    if (this.isBrowser && this.transferState.hasKey(stateKey)) {
      const state = this.transferState.get(stateKey, { companies: [], total: 0 });
      this.companies = state.companies;
      this.totalCompanies = state.total;
      this.isLoading = false;
      // Remove the state so subsequent navigations fetch fresh data
      this.transferState.remove(stateKey);
      return;
    }

    this.isLoading = true;
    const offset = this.currentPage * this.pageSize;

    this.companiesService
      .getCompanies(this.pageSize, offset, this.searchKeyword)
      .subscribe({
        next: (result) => {
          this.companies = result.companies;
          this.totalCompanies = result.total;
          this.isLoading = false;

          // On server, store the state for transfer to client
          if (this.isServer) {
            this.transferState.set(stateKey, {
              companies: result.companies,
              total: result.total,
            });
          }
        },
        error: () => {
          this.companies = [];
          this.totalCompanies = 0;
          this.isLoading = false;
        },
      });
  }

  private getStateKey(): ReturnType<typeof makeStateKey<{ companies: Company[]; total: number }>> {
    // Create unique key based on current search/page state
    return makeStateKey(`companies-${this.currentPage}-${this.pageSize}-${this.searchKeyword}`);
  }

  onSearch(): void {
    this.currentPage = 0;
    this.loadCompanies();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadCompanies();
    if (this.isBrowser) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  get totalPages(): number {
    return Math.ceil(this.totalCompanies / this.pageSize);
  }

  get pages(): number[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages: number[] = [];

    if (total <= 7) {
      for (let i = 0; i < total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(0);
      if (current > 3) {
        pages.push(-1); // ellipsis
      }
      for (let i = Math.max(1, current - 1); i <= Math.min(total - 2, current + 1); i++) {
        pages.push(i);
      }
      if (current < total - 4) {
        pages.push(-1); // ellipsis
      }
      pages.push(total - 1);
    }

    return pages;
  }
}
